import React, { useCallback, useMemo, memo, useEffect, useState, useRef } from 'react';
import { Table, Space, message, Card, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { EditIcon } from 'icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import useStaticPages from '../useStaticPages';
import { StaticPagesFilters } from './StaticPagesFilters';
import { EmptyState } from './EmptyState';
import { Button } from 'components';

export const StaticPagesList = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pages = useRecoilValue(getPage);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    staticPages,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingShoppingCenters,
    updateFilters,
    handlePageChange,
  } = useStaticPages();

  // 1. При монтуванні — підставляємо фільтри із URL
  useEffect(() => {
    const shopping_center_id = searchParams.get('shopping_center_id');
    const company_id = searchParams.get('company_id');
    const category_id = searchParams.get('category_id');
    const by_title = searchParams.get('by_title');
    
    const initialFilters = {};
    if (shopping_center_id) initialFilters.shopping_center_id = shopping_center_id;
    if (company_id) initialFilters.company_id = company_id;
    if (category_id) initialFilters.category_id = category_id;
    if (by_title) initialFilters.by_title = by_title;
    
    if (Object.keys(initialFilters).length > 0) {
      activeFiltersRef.current = initialFilters;
      updateFilters(initialFilters);
    }
    // eslint-disable-next-line
  }, []);

  const activeFiltersRef = useRef({});

  const handleFiltersChange = useCallback(
    (newFilters) => {
      const filteredFilters = {};
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          filteredFilters[key] = value;
        }
      });
      
      activeFiltersRef.current = filteredFilters;
      updateFilters(newFilters);
      
      const params = new URLSearchParams(searchParams);
      if (newFilters.shopping_center_id && newFilters.shopping_center_id !== null) {
        params.set('shopping_center_id', newFilters.shopping_center_id);
      } else {
        params.delete('shopping_center_id');
      }
      if (newFilters.company_id && newFilters.company_id !== null) {
        params.set('company_id', newFilters.company_id);
      } else {
        params.delete('company_id');
      }
      if (newFilters.category_id && newFilters.category_id !== null) {
        params.set('category_id', newFilters.category_id);
      } else {
        params.delete('category_id');
      }
      if (newFilters.by_title && newFilters.by_title !== null) {
        params.set('by_title', newFilters.by_title);
      } else {
        params.delete('by_title');
      }
      setSearchParams(params);
    },
    [updateFilters, searchParams, setSearchParams]
  );

  const columns = useMemo(
    () => [
      {
        title: <div style={{ textAlign: 'center' }}>{t('Titolo')}</div>,
        dataIndex: 'title',
        key: 'title',
        render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
        align: 'center',
      },
      // {
      //   title: <div style={{ textAlign: 'center' }}>{t('Slug')}</div>,
      //   dataIndex: 'slug',
      //   key: 'slug',
      //   align: 'center',
      // },
      {
        title: (
          <div style={{ textAlign: 'center' }}>{t('Outlet Village')}</div>
        ),
        dataIndex: 'shopping_center',
        key: 'shopping_center',
        align: 'center',
        render: (shoppingCenter) => {
          if (shoppingCenter?.title) {
            return shoppingCenter.title;
          }
          return '-';
        },
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Data creazione')}</div>,
        dataIndex: 'created_at',
        key: 'created_at',
        align: 'center',
        render: (date) => {
          if (date) {
            return new Date(date).toLocaleDateString('it-IT');
          }
          return '-';
        },
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Azioni')}</div>,
        key: 'actions',
        width: 180,
        align: 'center',
        render: (_, record) => {
          const buildUrlWithFilters = (path) => {
            const editParams = new URLSearchParams();
            const currentActiveFilters = activeFiltersRef.current;

            if (currentActiveFilters.shopping_center_id) {
              editParams.set(
                'shopping_center_id',
                currentActiveFilters.shopping_center_id,
              );
            }
            if (currentActiveFilters.company_id) {
              editParams.set('company_id', currentActiveFilters.company_id);
            }
            if (currentActiveFilters.category_id) {
              editParams.set('category_id', currentActiveFilters.category_id);
            }
            if (currentActiveFilters.by_title) {
              editParams.set('by_title', currentActiveFilters.by_title);
            }

            const queryString = editParams.toString();
            return queryString ? `${path}?${queryString}` : path;
          };

          return (
            <Space size='middle'>
              {(pages?.ability?.can_read !== false ||
                record.can_read !== false) && (
                <Tooltip title={t('Visualizza')}>
                  <Button
                    type='secondary'
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(buildUrlWithFilters(`/website/static_pages/${record.id}`));
                    }}
                  />
                </Tooltip>
              )}
              {record.can_update !== false && (
                <Tooltip title={t('Modifica')}>
                  <Button
                    type='secondary'
                    icon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        buildUrlWithFilters(
                          `/website/static_pages/edit/${record.id}`,
                        ),
                      );
                    }}
                  />
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    [t, navigate, pages?.ability?.can_read],
  );

  if (!loading && staticPages.length === 0) {
    return (
      <Card title={
        <span style={{ fontSize: '24px', fontWeight: '600' }}>
          {t('Pagine Statiche')}
        </span>
      }>
        <StaticPagesFilters
          onFiltersChange={handleFiltersChange}
          shoppingCenters={shoppingCenters}
          loadingShoppingCenters={loadingShoppingCenters}
          filters={filters}
        />
        <EmptyState />
      </Card>
    );
  }

  return (
    <Card title={
        <span style={{ fontSize: '24px', fontWeight: '600' }}>
          {t('Pagine Statiche')}
        </span>
      }>
      <StaticPagesFilters
        onFiltersChange={handleFiltersChange}
        shoppingCenters={shoppingCenters}
        loadingShoppingCenters={loadingShoppingCenters}
        filters={filters}
      />
      <Table
        columns={columns}
        dataSource={staticPages}
        loading={loading}
        rowKey='id'
        onRow={(record) => ({
          onClick: () => {
            if (!record?.id) {
              return;
            }
            const editParams = new URLSearchParams();
            const currentActiveFilters = activeFiltersRef.current;
            if (currentActiveFilters.shopping_center_id) {
              editParams.set(
                'shopping_center_id',
                currentActiveFilters.shopping_center_id,
              );
            }
            if (currentActiveFilters.company_id) {
              editParams.set('company_id', currentActiveFilters.company_id);
            }
            if (currentActiveFilters.category_id) {
              editParams.set('category_id', currentActiveFilters.category_id);
            }
            if (currentActiveFilters.by_title) {
              editParams.set('by_title', currentActiveFilters.by_title);
            }
            const queryString = editParams.toString();
            const viewUrl = queryString
              ? `/website/static_pages/${record.id}?${queryString}`
              : `/website/static_pages/${record.id}`;
            navigate(viewUrl);
          },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: false,
        }}
        onChange={(pagination) => {
          handlePageChange(pagination.current, pagination.pageSize);
        }}
        rowClassName={() => 'static-page-row'}
        style={{ marginTop: 8 }}
      />
      <style>{`
        .static-page-row:hover {
          background: #f5f7fa !important;
        }
      `}</style>
    </Card>
  );
}); 